import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/Login.css";
import { Form, Row, Col, Container, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import PopupMessage from "../../components/common/PopupMessage.js";
import { AuthContext } from "../../context/AuthContext.js";
import { login } from "../../services/api.js";
import { useSound } from "../../hooks/useSound.js";

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
    <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
  </svg>
);

const Login = () => {
  const { fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });
  const [showPassword, setShowPassword] = useState(false);
  const playLoginSound = useSound("/sounds/success.mp3");
  const playLoginSoundError = useSound("/sounds/error.mp3");

  const nubEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      if (!value) {
        setErrors((prev) => ({ ...prev, email: "البريد الإلكتروني مطلوب | Email is required" }));
      }
      else if (value.toLowerCase().endsWith("@nub.edu.eg")) {
        setErrors((prev) => ({
          ...prev,
          email: "غير مسموح بالإيميل الجامعي | University emails are not allowed",
        }));
      } else if (!nubEmailRegex.test(value)) {
        setErrors((prev) => ({ ...prev, email: "صيغة الإيميل غير صحيحة | Must be a valid email" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
    if (name === "password") {
      if (!value)
        setErrors((prev) => ({ ...prev, password: "كلمة المرور مطلوبة | Password is required" }));
      else if (value.length < 6 || value.length > 30)
        setErrors((prev) => ({
          ...prev,
          password: "كلمة المرور يجب أن تكون بين 6-30 حرف | Password must be 6-30 chars",
        }));
      else setErrors((prev) => ({ ...prev, password: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      errors.email ||
      errors.password ||
      !formData.email ||
      !formData.password
    ) {
      setPopup({
        show: true,
        type: "error",
        message: "برجاء مراجعة الأخطاء قبل المتابعة | Please fix the errors before submitting",
      });
      return;
    }
    setLoading(true);
    try {
      const response = await login(formData.email, formData.password);
      fetchUser();
      if (response?.status === 200) {
        setPopup({
          show: true,
          type: "success",
          message: response?.data?.message || "تم تسجيل الدخول بنجاح | Login successful",
        });
        playLoginSound();
        setTimeout(() => navigate("/"), 1500);
      } else {
        setPopup({
          show: true,
          type: "error",
          message: response?.message || "بيانات الدخول غير صحيحة | Invalid credentials",
        });
        playLoginSoundError();
      }
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: error.response?.data?.message || "حدث خطأ ما | An error occurred",
      });
      playLoginSoundError();
      if (error.response?.data?.message === "confirme Your email First") {
        setTimeout(() => {
          navigate("/auth/EmailVerification", {
            state: { email: formData.email },
          });
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="background-wrapper">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
      </div>

      <div className="login-advanced-container">
        <Container fluid="md" className="p-0">
          <Row className="justify-content-center m-0">
            <Col xs={12} lg={10} xl={9} className="p-0">
              <div className="login-card d-flex flex-column flex-md-row">
                {/* Brand Section (Desktop) */}
                <Col
                  md={5}
                  className="brand-section d-none d-md-flex text-white"
                >
                  <div
                    style={{
                      position: "relative",
                      zIndex: 2,
                      textAlign: "center",
                      margin: "auto" // عشان يتسنتر صح جوه الكولوم زي ما كان
                    }}
                  >
                    <img
                      src="/CampusPulsLogo.png"
                      alt="Logo"
                      style={{ width: "100px", marginBottom: "2rem" }}
                    />
                    <h2 className="fw-bold mb-2">المستقبل هنا</h2>
                    <h4 className="fw-bold mb-3 text-white-50" style={{ fontSize: "1.1rem" }}>Future is Here</h4>
                    <p className="text-white-50 mb-1">
                      انضم إلى المنصة الأحدث لإدارة حياتك الجامعية.
                    </p>
                    <p className="text-white-50" style={{ fontSize: "0.9rem" }}>
                      Join the most advanced campus management platform today.
                    </p>
                  </div>
                </Col>

                {/* Form Section */}
                <Col xs={12} md={7} className="form-section bg-transparent">
                  <div className="text-center d-md-none mobile-logo">
                    <img
                      src="/CampusPulsLogo.png"
                      alt="Logo"
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div className="mb-4 mb-md-5">
                    <h3 className="fw-bold text-white mb-1">
                      مرحباً بك | Welcome Back
                    </h3>
                    <p className="text-secondary small">
                      برجاء إدخال بياناتك | Please enter your details to sign in.
                    </p>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <div className="input-group-advanced">
                      <label className="input-label d-flex justify-content-between w-100">
                        <span>Email Address</span>
                        <span>البريد الإلكتروني</span>
                      </label>
                      <input
                        type="text"
                        name="email"
                        dir="ltr"
                        className={`advanced-input ${errors.email ? "is-invalid" : ""}`}
                        placeholder="ahmed@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      {errors.email && (
                        <small className="text-danger mt-1 d-block">
                          {errors.email}
                        </small>
                      )}
                    </div>

                    <div className="input-group-advanced">
                      <label className="input-label d-flex justify-content-between w-100">
                        <span>Password</span>
                        <span>كلمة المرور</span>
                      </label>

                      {/* WRAPPER FIX: Relative positioning for the input + eye */}
                      <div className="input-wrapper-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          dir="ltr"
                          className={`advanced-input with-icon ${errors.password ? "is-invalid" : ""}`}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="Toggle password visibility"
                        >
                          {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                      </div>

                      {errors.password && (
                        <small className="text-danger mt-1 d-block">
                          {errors.password}
                        </small>
                      )}
                    </div>

                    {/* Links Container - تم الحفاظ على الكلاسات الأصلية لتفادي ضرب التصميم */}
                    <div className="links-container">
                      <Link to="/auth/register" className="link-advanced">
                        إنشاء حساب | Create Account
                      </Link>
                      <Link
                        to="/forgot-password"
                        className="link-advanced link-highlight"
                      >
                        نسيت المرور؟ | Forgot Password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      className="btn-primary-advanced d-flex flex-column align-items-center justify-content-center gap-1"
                      disabled={loading}
                    >
                      {loading ? (
                        <span>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />{" "}
                          جاري التحقق... | Processing...
                        </span>
                      ) : (
                        <>
                          <span style={{ fontWeight: "700" }}>تسجيل الدخول</span>
                          <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>Sign In</span>
                        </>
                      )}
                    </button>
                  </Form>
                </Col>
              </div>
            </Col>
          </Row>
        </Container>

        {popup.show && (
          <PopupMessage
            type={popup.type}
            message={popup.message}
            onClose={() => setPopup({ ...popup, show: false })}
          />
        )}
      </div>
    </>
  );
};

export default Login;


// import "bootstrap/dist/css/bootstrap.min.css";
// import "../../assets/styles/Login.css";
// import { Form, Row, Col, Container, Spinner } from "react-bootstrap";
// import { Link, useNavigate } from "react-router-dom";
// import { useContext, useState } from "react";
// import PopupMessage from "../../components/common/PopupMessage.js";
// import { AuthContext } from "../../context/AuthContext.js";
// import { login } from "../../services/api.js";
// import { useSound } from "../../hooks/useSound.js";

// const EyeIcon = () => (
//   <svg
//     xmlns="http://www.w3.org/2000/svg"
//     width="20"
//     height="20"
//     fill="currentColor"
//     viewBox="0 0 16 16"
//   >
//     <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
//     <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
//   </svg>
// );

// const EyeSlashIcon = () => (
//   <svg
//     xmlns="http://www.w3.org/2000/svg"
//     width="20"
//     height="20"
//     fill="currentColor"
//     viewBox="0 0 16 16"
//   >
//     <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
//     <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
//     <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
//   </svg>
// );

// const Login = () => {
//   const { fetchUser } = useContext(AuthContext);
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({ email: "", password: "" });
//   const [errors, setErrors] = useState({ email: "", password: "" });
//   const [loading, setLoading] = useState(false);
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });
//   const [showPassword, setShowPassword] = useState(false);
//   const playLoginSound = useSound("/sounds/success.mp3");
//   const playLoginSoundError = useSound("/sounds/error.mp3");

//   // Validation Regex kept exactly as requested
//   const nubEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));

//     // if (name === "email") {
//     //   if (!value)
//     //     setErrors((prev) => ({ ...prev, email: "Email is required" }));
//     //   else if (!nubEmailRegex.test(value))
//     //     setErrors((prev) => ({ ...prev, email: "Must be a valid email" }));
//     //   else setErrors((prev) => ({ ...prev, email: "" }));
//     // }
//     if (name === "email") {
//       if (!value) {
//         setErrors((prev) => ({ ...prev, email: "Email is required" }));
//       }
//       // الشرط ده بيتأكد لو الإيميل بينتهي بالدومين بتاع الجامعة
//       else if (value.toLowerCase().endsWith("@nub.edu.eg")) {
//         setErrors((prev) => ({
//           ...prev,
//           email: "University emails are not allowed",
//         }));
//       } else if (!nubEmailRegex.test(value)) {
//         setErrors((prev) => ({ ...prev, email: "Must be a valid email" }));
//       } else {
//         setErrors((prev) => ({ ...prev, email: "" }));
//       }
//     }
//     if (name === "password") {
//       if (!value)
//         setErrors((prev) => ({ ...prev, password: "Password is required" }));
//       else if (value.length < 6 || value.length > 30)
//         setErrors((prev) => ({
//           ...prev,
//           password: "Password must be 6-30 chars",
//         }));
//       else setErrors((prev) => ({ ...prev, password: "" }));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     // Strict Validation Check before submission
//     if (
//       errors.email ||
//       errors.password ||
//       !formData.email ||
//       !formData.password
//     ) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: "Please fix the errors before submitting",
//       });
//       return;
//     }
//     setLoading(true);
//     try {
//       const response = await login(formData.email, formData.password);
//       fetchUser();
//       if (response?.status === 200) {
//         setPopup({
//           show: true,
//           type: "success",
//           message: response?.data?.message || "Login successful",
//         });
//         playLoginSound();
//         setTimeout(() => navigate("/"), 1500);
//       } else {
//         setPopup({
//           show: true,
//           type: "error",
//           message: response?.message || "Invalid credentials",
//         });
//         playLoginSoundError();
//       }
//     } catch (error) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: error.response?.data?.message || "An error occurred",
//       });
//       playLoginSoundError();
//       if (error.response?.data?.message === "confirme Your email First") {
//         setTimeout(() => {
//           navigate("/auth/EmailVerification", {
//             state: { email: formData.email },
//           });
//         }, 1500);
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <div className="background-wrapper">
//         <div className="orb orb-1"></div>
//         <div className="orb orb-2"></div>
//       </div>

//       <div className="login-advanced-container">
//         {/* Container Fluid to prevent horizontal scrolling issues */}
//         <Container fluid="md" className="p-0">
//           <Row className="justify-content-center m-0">
//             <Col xs={12} lg={10} xl={9} className="p-0">
//               <div className="login-card d-flex flex-column flex-md-row">
//                 {/* Brand Section (Desktop) */}
//                 <Col
//                   md={5}
//                   className="brand-section d-none d-md-flex text-white"
//                 >
//                   <div
//                     style={{
//                       position: "relative",
//                       zIndex: 2,
//                       textAlign: "center",
//                     }}
//                   >
//                     <img
//                       src="/CampusPulsLogo.png"
//                       alt="Logo"
//                       style={{ width: "100px", marginBottom: "2rem" }}
//                     />
//                     <h2 className="fw-bold mb-3">Future is Here</h2>
//                     <p className="text-white-50">
//                       Join the most advanced campus management platform today.
//                     </p>
//                   </div>
//                 </Col>

//                 {/* Form Section */}
//                 <Col xs={12} md={7} className="form-section bg-transparent">
//                   <div className="text-center d-md-none mobile-logo">
//                     <img
//                       src="/CampusPulsLogo.png"
//                       alt="Logo"
//                       style={{ width: "100%" }}
//                     />
//                   </div>

//                   <div className="mb-4 mb-md-5">
//                     <h3 className="fw-bold text-white mb-1">Welcome Back</h3>
//                     <p className="text-secondary small">
//                       Please enter your details to sign in.
//                     </p>
//                   </div>

//                   <Form onSubmit={handleSubmit}>
//                     <div className="input-group-advanced">
//                       <label className="input-label">Email Address</label>
//                       <input
//                         type="text"
//                         name="email"
//                         className={`advanced-input ${errors.email ? "is-invalid" : ""}`}
//                         placeholder="ahmed@example.com"
//                         value={formData.email}
//                         onChange={handleChange}
//                         disabled={loading}
//                       />
//                       {errors.email && (
//                         <small className="text-danger mt-1 d-block">
//                           {errors.email}
//                         </small>
//                       )}
//                     </div>

//                     <div className="input-group-advanced">
//                       <label className="input-label">Password</label>

//                       {/* WRAPPER FIX: Relative positioning for the input + eye */}
//                       <div className="input-wrapper-relative">
//                         <input
//                           type={showPassword ? "text" : "password"}
//                           name="password"
//                           className={`advanced-input with-icon ${errors.password ? "is-invalid" : ""}`}
//                           placeholder="••••••••"
//                           value={formData.password}
//                           onChange={handleChange}
//                           disabled={loading}
//                         />
//                         <button
//                           type="button"
//                           className="password-toggle"
//                           onClick={() => setShowPassword(!showPassword)}
//                           aria-label="Toggle password visibility"
//                         >
//                           {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
//                         </button>
//                       </div>

//                       {errors.password && (
//                         <small className="text-danger mt-1 d-block">
//                           {errors.password}
//                         </small>
//                       )}
//                     </div>

//                     {/* New Links Container Class */}
//                     <div className="links-container">
//                       <Link to="/auth/register" className="link-advanced">
//                         Create Account
//                       </Link>
//                       <Link
//                         to="/forgot-password"
//                         className="link-advanced link-highlight"
//                       >
//                         Forgot Password?
//                       </Link>
//                     </div>

//                     <button
//                       type="submit"
//                       className="btn-primary-advanced"
//                       disabled={loading}
//                     >
//                       {loading ? (
//                         <span>
//                           <Spinner
//                             as="span"
//                             animation="border"
//                             size="sm"
//                             className="me-2"
//                           />{" "}
//                           Processing...
//                         </span>
//                       ) : (
//                         "Sign In"
//                       )}
//                     </button>
//                   </Form>
//                 </Col>
//               </div>
//             </Col>
//           </Row>
//         </Container>

//         {popup.show && (
//           <PopupMessage
//             type={popup.type}
//             message={popup.message}
//             onClose={() => setPopup({ ...popup, show: false })}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default Login;
